"use client";

import { useTransition } from "react";
import { useParams, useRouter } from "next/navigation";
import ErrorMessage from "@hudson/ui/components/Error";
import Button from "@hudson/ui/components/FormFields/Button";
import useContent from "@lib/use_content";
import useReportCaughtError from "@components/ErrorBoundary/useReportCaughtError";
import { FALLBACK_MESSAGE, FALLBACK_START_OVER, FALLBACK_TRY_AGAIN } from "@components/ErrorBoundary/copy";

// Catches render crashes anywhere under a site (the universal/custom landing
// pages, the booking funnel, user account pages, manage-trip, frp) EXCEPT
// [siteId]/layout.tsx's own SSR render, which only the root boundary can catch.
// It renders inside that layout's <Providers>, so Apollo and the content tokens
// are available. Without it, a client render throw (e.g. a query killed mid-flight
// by an Apollo store reset) fell through to Next's default "Application error": a
// blank page.
//
// Recovery is deliberately two-pronged: 'Try again' re-runs the crashed segment
// in place (recovers a transient error), and 'Start over' hard-navigates to the
// booking start for a deterministic one. The raw error.message is reported to
// AppSignal but never shown — this is a customer-facing surface.
export default function SiteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  const params = useParams<{ siteId?: string }>();
  useReportCaughtError(error);

  // Only the message has a token on this release line; the two button labels
  // always come from the hardcoded fallback copy.
  const { data } = useContent(["beErrSystemError"]);
  const message = data?.content?.beErrSystemError || FALLBACK_MESSAGE;

  // router.refresh() re-runs the server render before reset() clears the boundary:
  // a bare reset() replays a Server Component crash (e.g. an async page or the
  // book/ layout under this segment) straight from the router cache without ever
  // retrying it. Batched in a transition so React commits the refetch and the
  // reset together; isPending feeds the button's loading/disabled state so a slow
  // refetch doesn't look like a dead button.
  const [isPending, startTransition] = useTransition();
  const tryAgain = () =>
    startTransition(() => {
      router.refresh();
      reset();
    });

  // Escape to the booking start — the boundary has replaced the page (header and
  // all), so this is the user's only in-app way out. A HARD navigation guarantees
  // the escape (no reset()/transition race) and, unlike the funnel boundary, does
  // NOT clear the cart: that state is unrelated to a non-funnel crash, so nuking
  // it would be gratuitous data loss. window.location is a raw navigation — Next
  // does not prepend basePath — so it carries the explicit process.env.basePath
  // prefix, the same shape every other hard navigation on this line uses.
  const startOver = () => {
    const siteId = params?.siteId ?? window.siteId;
    window.location.assign(`${process.env.basePath ?? ""}/${siteId}/book`);
  };

  return (
    <div className="min-h-[50vh] py-10 flex flex-col items-center justify-center gap-y-6">
      <ErrorMessage message={message} heavyStyle />
      <div className="flex flex-wrap items-center justify-center gap-4">
        <Button type="button" onClick={tryAgain} disabled={isPending} loading={isPending}>
          {FALLBACK_TRY_AGAIN}
        </Button>
        <Button type="button" onClick={startOver}>
          {FALLBACK_START_OVER}
        </Button>
      </div>
    </div>
  );
}
